import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ selectedShingles, onRemove, onClear, isOpen, onClose }) => {
  if (!isOpen || selectedShingles?.length === 0) return null;

  const comparisonFeatures = [
    'Brand',
    'Material Type',
    'Warranty',
    'Wind Rating',
    'Fire Rating',
    'Algae Resistance',
    'Impact Resistance',
    'Energy Star',
    'Price Range',
    'Installation Time'
  ];

  const getFeatureValue = (shingle, feature) => {
    switch (feature) {
      case 'Brand':
        return shingle?.brand;
      case 'Material Type':
        return shingle?.materialType;
      case 'Warranty':
        return shingle?.warranty;
      case 'Wind Rating':
        return shingle?.windRating;
      case 'Fire Rating':
        return shingle?.specifications?.fireRating || 'Class A';
      case 'Algae Resistance':
        return shingle?.features?.includes('Algae Resistant') ? 'Yes' : 'No';
      case 'Impact Resistance':
        return shingle?.features?.includes('Impact Resistant') ? 'Yes' : 'No';
      case 'Energy Star':
        return shingle?.features?.includes('Energy Star') ? 'Yes' : 'No';
      case 'Price Range':
        return `$${shingle?.priceRange?.min} - $${shingle?.priceRange?.max}`;
      case 'Installation Time':
        return shingle?.installationTime || '1-2 days';
      default:
        return '-';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">Compare Shingles</h2>
            <p className="text-text-secondary">
              Comparing {selectedShingles?.length} shingle{selectedShingles?.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClear}>
              Clear All
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={24} />
            </Button>
          </div>
        </div>

        {/* Comparison Content */}
        <div className="overflow-auto max-h-[calc(90vh-140px)]">
          <div className="p-6">
            {/* Product Headers */}
            <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${selectedShingles?.length}, 1fr)` }}>
              <div></div>
              {selectedShingles?.map((shingle, index) => (
                <div key={shingle?.id} className="text-center">
                  <div className="relative mb-3">
                    <Image
                      src={shingle?.image}
                      alt={shingle?.imageAlt}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => onRemove(shingle)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-error text-white rounded-full flex items-center justify-center hover:bg-error/80 transition-colors"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                  <h3 className="font-semibold text-text-primary text-sm">{shingle?.name}</h3>
                  <p className="text-text-secondary text-xs">{shingle?.brand}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Icon name="Star" size={12} className="text-warning fill-current" />
                    <span className="text-xs">{shingle?.rating}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <div className="space-y-1">
              {comparisonFeatures?.map((feature, index) => (
                <div 
                  key={feature}
                  className={`grid gap-4 py-3 px-2 rounded-lg ${
                    index % 2 === 0 ? 'bg-surface' : 'bg-white'
                  }`}
                  style={{ gridTemplateColumns: `200px repeat(${selectedShingles?.length}, 1fr)` }}
                >
                  <div className="font-medium text-text-primary">{feature}</div>
                  {selectedShingles?.map((shingle) => (
                    <div key={shingle?.id} className="text-text-secondary text-sm text-center">
                      {getFeatureValue(shingle, feature)}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="mt-8 grid gap-4" style={{ gridTemplateColumns: `200px repeat(${selectedShingles?.length}, 1fr)` }}>
              <div></div>
              {selectedShingles?.map((shingle) => (
                <div key={shingle?.id} className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    onClick={() => window.location.href = '/gallery'}
                  >
                    View Gallery
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    onClick={() => window.location.href = '/instant-estimate'}
                  >
                    Get Quote
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTool;